import { useState } from 'react'
import { useLocalParticipant } from '@livekit/components-react'

import { t } from './i18n'
import type { UnmuteRequest } from './useClassroomSignals'

/**
 * "Your teacher asked you to turn your microphone on."
 *
 * This exists so the server never has to. A child's microphone and camera come
 * back on by their own tap, never remotely — see Meetings::Moderation for the
 * server half of that decision.
 *
 * Written for an eight-year-old: one sentence, one obvious green button, and a
 * clear way to say no. "Not now" is a real answer, not a nag to dismiss.
 */
export function UnmutePrompt({
  request,
  onDismiss,
}: {
  request: UnmuteRequest
  onDismiss: () => void
}) {
  const { localParticipant } = useLocalParticipant()
  const isMic = request.source === 'microphone'

  const [failed, setFailed] = useState(false)

  const accept = async () => {
    setFailed(false)
    try {
      if (isMic) await localParticipant?.setMicrophoneEnabled(true)
      else await localParticipant?.setCameraEnabled(true)
    } catch {
      // Dismissing here was the bug: the child taps "Turn on microphone", the
      // device refuses (unplugged headset, revoked permission, another app
      // holding it), the prompt vanishes as though it had worked — and the
      // tutor, who can see they are still muted, asks again into the same
      // silence. The prompt now STAYS, and says so.
      setFailed(true)
      return
    }
    onDismiss()
  }

  return (
    <div className="unmute-prompt" role="alertdialog" aria-labelledby="unmute-prompt-title">
      <span className="unmute-prompt__icon" aria-hidden="true">
        {isMic ? '🎤' : '📷'}
      </span>
      <p className="unmute-prompt__text" id="unmute-prompt-title">
        {failed
          ? t(isMic ? 'unmute.failedMic' : 'unmute.failedCamera')
          : t(isMic ? 'unmute.askedMic' : 'unmute.askedCamera')}
      </p>
      <div className="unmute-prompt__actions">
        <button type="button" className="pill-button pill-button--accent" onClick={accept}>
          {t(isMic ? 'unmute.turnOnMic' : 'unmute.turnOnCamera')}
        </button>
        <button type="button" className="pill-button pill-button--quiet" onClick={onDismiss}>
          {t('unmute.notNow')}
        </button>
      </div>
    </div>
  )
}
